import supabase from "../supabase-client";
import { buildRentLines, addDaysISO, cfdiUuidOf } from "./invoicing";

const API_URL = "https://api.gomoverent.com/v2/alegra/proxy.php";
const API_TOKEN = "h8ISmsSIwTvfl8Tny2nr0dFh2nz3rmiCzBMjtCJOJqn7TF6Y4wnch7uI1FI3ArlZP0wfMsGkEuqNXUzaX9wYSL5bnn9CzB1DkWAMFQVCBWBhRwAXn29M96YYxcp1bmfN";

const alegraRequest = async (endpoint, method = "GET", body = null) => {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
    body: JSON.stringify({ endpoint, method, body }),
  });
  const data = await res.json();
  if (!data.success) {
    const msg = typeof data.error === "object" ? data.error.message || JSON.stringify(data.error) : data.error || "Alegra error";
    throw new Error(msg);
  }
  return data.data;
};

/**
 * Calculate the next trigger date for a group based on its periodicity
 */
export function getNextTriggerDate(periodicity, weeklyDay, fromDate = new Date()) {
  const d = new Date(fromDate);
  d.setHours(0, 0, 0, 0);

  switch (periodicity) {
    case "monthly_1st": {
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      return next;
    }
    case "monthly_last": {
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 0); // last day of current month
      if (next <= d) {
        return new Date(d.getFullYear(), d.getMonth() + 2, 0);
      }
      return next;
    }
    case "weekly": {
      const day = weeklyDay || 1;
      const diff = (day - d.getDay() + 7) % 7;
      const next = new Date(d);
      next.setDate(d.getDate() + (diff === 0 ? 7 : diff));
      return next;
    }
    case "biweekly": {
      const dayOfMonth = d.getDate();
      if (dayOfMonth < 15) return new Date(d.getFullYear(), d.getMonth(), 15);
      return new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    default:
      return new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
}

/**
 * Check if today is a trigger date for the given periodicity
 */
export function isTriggerDate(periodicity, weeklyDay, today = new Date()) {
  const d = today.getDate();
  const dow = today.getDay();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

  switch (periodicity) {
    case "monthly_1st": return d === 1;
    case "monthly_last": return d === lastDay;
    case "weekly": return dow === (weeklyDay || 1);
    case "biweekly": return d === 1 || d === 15;
    default: return false;
  }
}

/**
 * Calculate billing period for a reservation
 */
export function calculateBillingPeriod(reservation, group, triggerDate) {
  const resStart = new Date(reservation.date_range?.replace(/[\[\]()]/g, "").split(",")[0]?.replace(/"/g, "").trim().substring(0, 10));

  // Find last invoiced period for this reservation
  const lastPeriodTo = reservation.last_period_to ? new Date(reservation.last_period_to) : null;

  let periodFrom, periodTo;

  if (group.billing_period === "transcurrido") {
    // Past usage: from last invoice end (or start) to trigger date
    if (lastPeriodTo) {
      periodFrom = new Date(lastPeriodTo);
      periodFrom.setDate(periodFrom.getDate() + 1);
    } else {
      periodFrom = new Date(resStart);
    }
    periodTo = new Date(triggerDate);
  } else {
    // Posterior (future): from trigger date to next trigger
    periodFrom = new Date(triggerDate);
    periodTo = getNextTriggerDate(group.periodicity, group.weekly_day, triggerDate);
    periodTo.setDate(periodTo.getDate() - 1);
  }

  // Don't go before reservation start
  if (periodFrom < resStart) periodFrom = new Date(resStart);

  return {
    periodFrom: periodFrom.toISOString().substring(0, 10),
    periodTo: periodTo.toISOString().substring(0, 10),
  };
}

/**
 * Calculate days in a period
 * Full month = 30 days, partial = actual days
 */
export function calculateDaysInPeriod(periodFrom, periodTo) {
  const from = new Date(periodFrom);
  const to = new Date(periodTo);

  // Check if it's a full month (1st to last day, or 1st to 30th+)
  if (from.getDate() === 1) {
    const nextMonth1st = new Date(from.getFullYear(), from.getMonth() + 1, 1);
    const lastDayOfMonth = new Date(nextMonth1st - 1);
    if (to >= lastDayOfMonth || to.getDate() >= 28) {
      return 30; // Full month = always 30
    }
  }

  // Also check 15th to 14th (biweekly full period)
  // For non-full-month periods, use actual days
  const diffMs = to - from;
  return Math.max(1, Math.ceil(diffMs / 86400000) + 1); // inclusive
}

/**
 * Generate an invoice for all reservations in a group
 */
export async function generateGroupInvoice(group, triggerDate, stamp = false) {
  // Fetch active reservations in this group
  const { data: reservations } = await supabase
    .from("reservations")
    .select("*, car(plate, model(name, brand(name)), alegra_item_id)")
    .eq("invoicing_group_id", group.id)
    .eq("status", "active");

  if (!reservations || reservations.length === 0) {
    throw new Error("No hay reservas activas en este grupo");
  }

  // Get last invoiced period per reservation. Considers both directly-linked
  // invoices AND multi-reservation invoices referencing this reservation via
  // invoice_reservations.
  for (const res of reservations) {
    const [direct, joined] = await Promise.all([
      supabase
        .from("invoices_issued")
        .select("period_to")
        .eq("reservation_id", res.id)
        .not("period_to", "is", null)
        .neq("status", "void")
        .order("period_to", { ascending: false })
        .limit(1),
      supabase
        .from("invoice_reservations")
        .select("invoice_id, period_to")
        .eq("reservation_id", res.id),
    ]);
    let lastPeriodTo = direct.data?.[0]?.period_to || null;
    const joinedIds = [
      ...new Set((joined.data || []).map((j) => j.invoice_id)),
    ];
    if (joinedIds.length > 0) {
      const { data: joinedInv } = await supabase
        .from("invoices_issued")
        .select("id, period_to")
        .in("id", joinedIds)
        .neq("status", "void");
      // Multi-res invoices carry the period per line (invoice_reservations);
      // fall back to the invoice-level period for older rows.
      const live = new Map((joinedInv || []).map((i) => [i.id, i.period_to]));
      let joinedLatest = null;
      for (const j of joined.data || []) {
        if (!live.has(j.invoice_id)) continue;
        const pt = j.period_to || live.get(j.invoice_id);
        if (pt && (!joinedLatest || pt > joinedLatest)) joinedLatest = pt;
      }
      if (
        joinedLatest &&
        (!lastPeriodTo || new Date(joinedLatest) > new Date(lastPeriodTo))
      ) {
        lastPeriodTo = joinedLatest;
      }
    }
    res.last_period_to = lastPeriodTo;
  }

  // Calculate periods and amounts. Each reservation may yield multiple rent
  // lines if its period spans a rate change (see reservation_price_periods).
  const lineItems = [];
  const reservationLinks = [];
  // Aggregate discount consumed across the group's reservations (per-reservation
  // attribution isn't tracked on this multi-res auto path — see discount_applied).
  let groupDiscountApplied = 0;
  let groupDiscountDays = 0;

  for (const res of reservations) {
    const { periodFrom, periodTo } = calculateBillingPeriod(res, group, triggerDate);
    const plate = res.car?.plate || "?";
    const model = res.car?.model?.name || "";
    const alegraItemId = res.car?.alegra_item_id || null;

    const rent = await buildRentLines(res.id, periodFrom, periodTo, {
      plate,
      alegraItemId,
      isSublease: !!res.is_sublease,
    });
    groupDiscountApplied += rent.discountApplied || 0;
    groupDiscountDays += rent.discountDays || 0;

    let resAmount = 0;
    for (const line of rent.lines) {
      // Honor any residual per-unit discount buildRentLines distributed.
      const amount = (line.unitPrice - (line.discount || 0)) * line.quantity;
      resAmount += amount;
      lineItems.push({
        alegraItemId,
        description: `${plate} - ${model} - Periodo ${line.sectionPeriodFrom} al ${addDaysISO(line.sectionPeriodTo, 1)}`,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        discount: line.discount || 0,
        amount,
        reservationId: res.id,
        periodFrom: line.sectionPeriodFrom,
        periodTo: line.sectionPeriodTo,
      });
    }

    reservationLinks.push({ reservationId: res.id, periodFrom, periodTo, amount: resAmount });
  }

  // Find the company for billing
  const companyId = group.company_id;
  if (!companyId) throw new Error("El grupo no tiene empresa asignada");

  const { data: companyData } = await supabase
    .from("companies")
    .select("*")
    .eq("id", companyId)
    .single();

  if (!companyData) throw new Error("Empresa no encontrada");

  // Find or create Alegra contact
  let contactId;
  try {
    const contacts = await alegraRequest(`contacts?identification=${companyData.rfc}`, "GET");
    if (Array.isArray(contacts) && contacts.length > 0) {
      contactId = contacts[0].id;
    }
  } catch {}

  if (!contactId) throw new Error("No se encontró contacto en Alegra para " + companyData.name);

  // Build Alegra line items
  const alegraItems = lineItems.map((item) => {
    const priceBeforeTax = Math.round((item.unitPrice / 1.16) * 100) / 100;
    const line = {
      price: priceBeforeTax,
      quantity: item.quantity,
      description: item.description,
      tax: [{ id: 2 }],
    };
    if (item.alegraItemId) line.id = item.alegraItemId;
    return line;
  });

  // Calculate total
  const total = lineItems.reduce((sum, item) => sum + item.amount, 0);

  // Invoice dates
  const invoiceDate = triggerDate.toISOString ? triggerDate.toISOString().substring(0, 10) : triggerDate;
  const dueDateObj = new Date(invoiceDate);
  dueDateObj.setDate(dueDateObj.getDate() + (group.credit_enabled ? group.credit_days : 0));
  const dueDate = dueDateObj.toISOString().substring(0, 10);

  // Create invoice in Alegra
  const invoicePayload = {
    date: invoiceDate,
    dueDate: dueDate,
    client: { id: String(contactId) },
    items: alegraItems,
    paymentMethod: companyData.payment_form || "transfer",
    paymentType: companyData.payment_type_cfdi || "PUE",
    cfdiUse: companyData.cfdi_use || "G03",
  };

  if (stamp) {
    invoicePayload.stamp = { generateStamp: true, version: "4.0" };
  }

  const invoice = await alegraRequest("invoices", "POST", invoicePayload);

  // Save to our DB
  const subtotal = total / 1.16;
  const tax = total - subtotal;

  const { data: savedInvoice } = await supabase.from("invoices_issued").insert({
    alegra_id: String(invoice.id),
    cfdi_uuid: cfdiUuidOf(invoice),
    invoice_number: invoice.numberTemplate?.fullNumber || invoice.id,
    customer_id: null,
    company_id: companyId,
    date: invoiceDate,
    subtotal: Math.round(subtotal * 100) / 100,
    tax: Math.round(tax * 100) / 100,
    total,
    status: stamp ? (invoice.status || "open") : "draft",
    stamped: stamp,
    cfdi_use: companyData.cfdi_use || "G03",
    payment_method: companyData.payment_form || "transfer",
    payment_type: companyData.payment_type_cfdi || "PUE",
    discount_applied: Math.round(groupDiscountApplied * 100) / 100,
    discount_days: groupDiscountDays,
  }).select("id").single();

  // Per-line reservation links (same shape as /invoicing/create's multi-res
  // invoices) so payments, portions and gap/overlap checks see this invoice.
  // period_to is inclusive (last billed day).
  if (savedInvoice?.id) {
    const rows = lineItems.map((item) => ({
      invoice_id: savedInvoice.id,
      reservation_id: item.reservationId,
      line_description: item.description,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      discount: item.discount || 0,
      period_from: item.periodFrom,
      period_to: item.periodTo,
    }));
    if (rows.length > 0) {
      const { error: linkErr } = await supabase.from("invoice_reservations").insert(rows);
      if (linkErr) console.error("invoice_reservations insert failed", linkErr);
    }
  }

  // Link invoice to reservations and save periods
  for (const link of reservationLinks) {
    // Save period on a per-reservation basis
    await supabase.from("auto_invoice_runs").insert({
      invoicing_group_id: group.id,
      trigger_date: invoiceDate,
      period_from: link.periodFrom,
      period_to: link.periodTo,
      invoice_id: savedInvoice?.id || null,
      status: stamp ? "stamped" : "draft",
    });
  }

  return {
    invoiceId: invoice.id,
    invoiceNumber: invoice.numberTemplate?.fullNumber || invoice.id,
    total,
    lineItems: lineItems.length,
    stamped: stamp,
  };
}
